import { Box, Button, Typography } from "@mui/material";
import { cityScraperList } from "../../../data/cityScraperList";
import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import {
  fetchJaxFields,
  setCityFailedErrorText,
  setPermitPanelOpen,
  setScheduleOfValues,
  setUsingTemplates,
} from "../../../store/actions/uiActions/myPermitsActions";
import JaxModal from "../../noc/realEstateComponents/JaxModal";

const CitySiteTab = () => {
  const {
    selectedRow,
    prevRowID,
    cityFailedErrorText,
    selectedPermitType,
    activeTab,
  } = useSelector((state) => state.myPermitsComponent);
  const { permitPanelOpen } = useSelector(
    (state) => state.myPermitsComponent.citySite
  );

  const dispatch = useDispatch();

  // Get the correct permit data based on selectedPermitType
  const getCurrentPermitData = () => {
    if (!selectedRow || !selectedPermitType) return selectedRow;

    // If it's the main permit type, return main selectedRow
    if (selectedPermitType === selectedRow.permit_type) {
      return selectedRow;
    }

    // If it's an additional permit, find it in additional_permits
    if (
      selectedRow.additional_permits &&
      selectedRow.additional_permits[selectedPermitType]
    ) {
      const additionalPermit =
        selectedRow.additional_permits[selectedPermitType];
      if (Array.isArray(additionalPermit)) {
        // If it's an array, get the first one (most recent)
        return additionalPermit[0];
      } else {
        // If it's a single object
        return additionalPermit;
      }
    }

    return selectedRow;
  };

  const currentPermitData = getCurrentPermitData();

  const [animateOut, setAnimateOut] = useState(false);
  const [startCoords, setStartCoords] = useState({ top: 0, left: 0 });

  const buttonRef = useRef(null);

  const handleClosePanel = () => {
    dispatch(
      setScheduleOfValues({
        rows: [],
        scheduleCreated: false,
        remainingAmount: "―",
        remainingPercentage: "―",
        totalCost: 0,
        totalCostDisplay: "",
        editSchedule: false,
        scheduleOfValuesDocLoading: false,
        loadedLogo: "",
        downloadLoading: false,
        savingTemplateLoading: false,
      })
    );
    dispatch(
      setUsingTemplates({
        isUsingTemplate: false,
        templateID: "",
        templateName: "",
        removedTemplate: false,
      })
    );
    setAnimateOut(true);
    setTimeout(() => {
      dispatch(setPermitPanelOpen(false));
    }, 600); // Match with animation duration
  };

  const handleOpenPanel = () => {
    const rect = buttonRef.current.getBoundingClientRect();
    setStartCoords({
      top: rect.top + rect.height / 2,
      left: rect.left + rect.width / 2,
    });
    dispatch(setPermitPanelOpen(true));
    setAnimateOut(false);
  };

  const renderedCityFieldsRef = useRef(null);

  useEffect(() => {
    const shouldFetch =
      currentPermitData?.noc_created &&
      currentPermitData?.connect_to_city?.is_connected &&
      currentPermitData?.connect_to_city?.permit_url &&
      currentPermitData?.task_id &&
      currentPermitData.task_id !== prevRowID &&
      !renderedCityFieldsRef?.current;

    if (shouldFetch) {
      renderedCityFieldsRef.current = true;
      dispatch(fetchJaxFields());
    }
  }, [
    currentPermitData?.task_id,
    currentPermitData?.noc_created,
    currentPermitData?.connect_to_city?.is_connected,
    currentPermitData?.connect_to_city?.permit_url,
    prevRowID,
    dispatch,
  ]);

  useEffect(() => {
    if (cityFailedErrorText) {
      dispatch(setCityFailedErrorText(""));
    }
  }, [cityFailedErrorText, dispatch]);

  return (
    <>
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          flexDirection: "column",
          mt: 1,
        }}
      >
        <img
          alt="city_img"
          src="/img/website_img/city_img.png"
          style={{
            width: "100%",
            maxWidth: "5rem",
            height: "auto",
            borderRadius: "20px",
          }}
        />
        <Typography align="center" px={1} variant="h5" mt={1} color="primary">
          City Permitting
        </Typography>

        {!cityScraperList.includes(selectedRow?.city) ? (
          <Box mt={6}>
            <Typography align="center" px={2} variant="h6" color="primary">
              Unfortunately, we do not currently support permitting automation
              for this city. Please check back later for future updates.
            </Typography>
          </Box>
        ) : cityScraperList.includes(selectedRow?.city) &&
          !currentPermitData?.connect_to_city?.permit?.permit_id ? (
          <Box mt={6}>
            <Typography align="center" px={2} variant="h6" color="primary">
              Permit creation requests have not yet been initiated.
            </Typography>
          </Box>
        ) : cityScraperList.includes(selectedRow?.city) &&
          currentPermitData?.connect_to_city?.permit?.permit_id ? (
          <>
            <Typography
              align="center"
              px={1}
              mb={1}
              variant="body2"
              fontWeight="bold"
              color="primary"
            >
              {currentPermitData?.connect_to_city?.permit?.full_permit_number}
            </Typography>
            {currentPermitData?.connect_to_city?.status &&
              Object.entries(currentPermitData.connect_to_city.status).map(
                ([key, value], idx) => {
                  const label = key
                    .split("_")
                    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                    .join(" ");

                  return (
                    <Box
                      key={idx}
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      gap={1}
                      px={2}
                      py={0}
                    >
                      {value ? (
                        <CheckCircleIcon
                          sx={{ color: "#10b981", fontSize: "1rem" }}
                        />
                      ) : (
                        <CancelIcon
                          sx={{ color: "#ef4444", fontSize: "1rem" }}
                        />
                      )}
                      <Typography variant="body2" fontWeight="bold">
                        {label}
                      </Typography>
                    </Box>
                  );
                }
              )}
            {activeTab !== 3 &&
              activeTab !== 4 &&
              !currentPermitData?.notarized &&
              !currentPermitData?.noc_notarized &&
              !currentPermitData?.pp_notarized &&
              !currentPermitData?.processing_permit?.notarization?.started && (
                <Button
                  ref={buttonRef}
                  variant="contained"
                  sx={{ mt: 2, mb: 1 }}
                  size="small"
                  color="primary"
                  onClick={handleOpenPanel}
                >
                  Open City Permit Panel
                </Button>
              )}
            <Button
              variant="contained"
              size="small"
              color="success"
              href={currentPermitData?.connect_to_city?.permit_url}
              target="_blank"
            >
              Visit City Permitting Application
            </Button>
          </>
        ) : null}
      </Box>
      {permitPanelOpen && (
        <JaxModal
          handleClosePanel={handleClosePanel}
          startCoords={startCoords}
          animateOut={animateOut}
        />
      )}
    </>
  );
};

export default React.memo(CitySiteTab);
